import React, { FC, useCallback, useEffect, useState } from 'react';
import firebase from 'firebase/app';
import { adminApi } from 'util/adminApi';
import { serverQuery } from 'util/serverRead';
import { ui } from 'util/ui';
import { Panel, SectionHead, TRow, Cell, AdminButton, Pill, Notice } from './adminUi';

/**
 * Activity tab: every change to the library and every admin action, newest first,
 * recorded by the server. Deleted records can be put back from here.
 */

export type Entry = {
  id: string;
  at: Date | null;
  kind: 'data' | 'admin';
  action: string;
  collection?: string;
  docId?: string;
  title?: string | null;
  changedFields?: string[];
  actor?: { type: string; id: string | null; email?: string | null };
  targetEmail?: string | null;
  from?: string | null;
  to?: string | null;
  restoredAt?: unknown;
  before?: { _omitted?: boolean } | null;
  after?: { bin?: unknown } | null;
};

const PAGE = 100;
const SINGULAR: Record<string, string> = {
  projects: 'project',
  records: 'record',
  videos: 'video',
  notes: 'note',
  assets: 'asset',
  gallery: 'gallery item',
  images: 'image',
  categories: 'category',
  defaults: 'defaults',
};

const tierName = (cls?: string | null) => (cls === 'ARCHIVE' ? 'cold storage' : cls === 'STANDARD' ? 'hot storage' : cls || '?');

/** Turns a log entry from its Firestore document into an Entry. */
export const toEntry = (d: firebase.firestore.DocumentSnapshot): Entry => {
  const data = d.data() || {};
  return { id: d.id, ...data, at: data.at ? data.at.toDate() : null } as Entry;
};

/** Who made a change: their email when known, "System" for the server. */
export const actorName = (e: Entry, emails: Record<string, string>) => {
  if (!e.actor) return '';
  if (e.actor.email) return e.actor.email;
  if (e.actor.id && emails[e.actor.id]) return emails[e.actor.id];
  // Server writes (restores, storage moves) carry a Google service account rather than a person.
  if (e.actor.type === 'service_account' || e.actor.type === 'system' || /gserviceaccount\.com$/.test(e.actor.id || '')) return 'System';
  return e.actor.id || e.actor.type;
};

/** A deletion that can be put back from its saved copy. */
export const canRestore = (e: Entry) => e.kind === 'data' && e.action === 'delete' && !e.restoredAt && !!e.before && !e.before._omitted;

export const describe = (e: Entry): string => {
  // Entries written by the server itself (storage tiers, 5 Nov 2026).
  if ((e.kind as string) === 'system' || e.action.startsWith('storage.')) {
    const x = e as Entry & {
      summary?: { moved?: number };
      moved?: number;
      queued?: number;
      checked?: number;
      result?: string;
      deleted?: number;
      putBack?: number;
      due?: number;
      max?: number;
    };
    switch (e.action) {
      case 'bin.purge':
        return `Deleted items: ${x.deleted ?? 0} file(s) deleted for good after 30 days${x.putBack ? `, ${x.putBack} back in a project so kept` : ''}`;
      case 'bin.purgeHeld':
        return `Deleted items: ${x.due ?? '?'} files were due, more than the nightly limit of ${x.max ?? '?'}, so nothing was deleted`;
      case 'storage.tier':
        return `Moved video “${e.docId}” to ${tierName(e.to)}${x.result === 'check' ? ' (needs a check)' : ''}`;
      case 'storage.tierScan':
        return `Daily storage check: ${x.queued ?? 0} file(s) queued to move (of ${x.checked ?? 0} checked)`;
      case 'storage.tiers':
        return `Storage fix: ${x.summary?.moved ?? x.moved ?? 0} file(s) moved to the right tier`;
      default:
        return e.action;
    }
  }
  if (e.kind === 'admin') {
    const who = e.targetEmail || e.docId || '';
    switch (e.action) {
      case 'user.create':
        return `Created account ${who} (${e.to})`;
      case 'user.disable':
        return `Disabled ${who}`;
      case 'user.enable':
        return `Enabled ${who}`;
      case 'user.setRole':
        return `Changed ${who} from ${e.from || 'no access'} to ${e.to || 'no access'}`;
      case 'user.delete':
        return `Deleted the sign-in account ${who}`;
      case 'user.resetLink':
        return `Made a password reset link for ${who}`;
      case 'record.restore':
        return `Restored ${SINGULAR[e.collection || ''] || e.collection} ${e.docId}`;
      case 'bin.fill':
        return `Moved ${((e as Entry & { binned?: number }).binned ?? 0).toLocaleString()} file(s) with no project to Deleted items`;
      case 'bin.restore':
        return `Restored ${SINGULAR[e.collection || ''] || e.collection} “${e.docId}” from Deleted items`;
      default:
        return e.action;
    }
  }
  const thing = `${SINGULAR[e.collection || ''] || e.collection} “${e.title || e.docId}”`;
  if (e.action === 'create') return `Added ${thing}`;
  if (e.action === 'delete') return `Deleted ${thing}`;
  if (e.action === 'update' && e.changedFields && e.changedFields.length === 1 && e.changedFields[0] === 'bin') {
    return e.after && e.after.bin ? `Moved ${thing} to Deleted items` : `Took ${thing} out of Deleted items`;
  }
  return `Edited ${thing}${e.changedFields?.length ? ` (${e.changedFields.join(', ')})` : ''}`;
};

const ActivityLog: FC = () => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [onlyDeletes, setOnlyDeletes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false); // then the list isn't shown as empty

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    let gotList = false;
    try {
      let q = firebase.firestore().collection('activity').orderBy('at', 'desc').limit(PAGE);
      if (onlyDeletes) {
        q = firebase.firestore().collection('activity').where('action', '==', 'delete').orderBy('at', 'desc').limit(PAGE);
      }
      const snap = await serverQuery(q); // from the database, so a slow connection errors instead of showing nothing
      setEntries(snap.docs.map(toEntry));
      gotList = true;
      const users = await adminApi.listUsers();
      setEmails(Object.fromEntries(users.users.map((u) => [u.uid, u.email || u.uid])));
      setMessage(null);
    } catch (e) {
      setLoadFailed(!gotList);
      setMessage(gotList ? (e as Error).message : `The activity didn't load. ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [onlyDeletes]);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (e: Entry) => {
    if (!window.confirm(`Put back ${describe(e).replace(/^Deleted /, '')} exactly as it was before it was deleted?`)) return;
    setBusy(e.id);
    try {
      const res = await adminApi.restoreDeleted(e.id);
      setMessage(`Restored ${res.restored}.${res.note ? ' ' + res.note : ''}`);
      await load();
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const who = (e: Entry) => actorName(e, emails);

  const COLS = '150px minmax(0,3fr) minmax(0,1.5fr) 110px';
  return (
    <section style={{ marginBottom: 28 }}>
      <SectionHead title="Activity" note={`Latest ${PAGE} changes, recorded by the server. Nobody can edit or remove this log from the app.`}>
        <div role="group" aria-label="Show" style={{ display: 'inline-grid', gridTemplateColumns: 'repeat(2, auto)', gap: 4, padding: 4, background: ui.subtle, borderRadius: 12 }}>
          {[false, true].map((del) => {
            const on = onlyDeletes === del;
            return (
              <button
                key={String(del)}
                type="button"
                aria-pressed={on}
                onClick={() => setOnlyDeletes(del)}
                style={{
                  height: 32,
                  padding: '0 14px',
                  border: 'none',
                  borderRadius: 9,
                  background: on ? ui.surface : 'transparent',
                  boxShadow: on ? '0 1px 3px rgba(0,0,0,0.14)' : 'none',
                  color: on ? ui.ink : ui.muted,
                  fontFamily: ui.font,
                  fontSize: 13,
                  fontWeight: on ? 600 : 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {del ? 'Deletions only' : 'All changes'}
              </button>
            );
          })}
        </div>
      </SectionHead>
      {message && (
        <Notice tone={loadFailed ? 'error' : 'info'}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            <span style={{ flex: '1 1 260px' }}>{message}</span>
            {loadFailed && <AdminButton onClick={load}>Try again</AdminButton>}
          </div>
        </Notice>
      )}
      <Panel>
        <TRow head columns={COLS}>
          <Cell>When</Cell>
          <Cell>What</Cell>
          <Cell>Who</Cell>
          <Cell />
        </TRow>
        {loading && !entries.length && <p style={{ margin: 0, padding: '14px 16px', fontSize: 14, color: ui.muted }}>Loading…</p>}
        {!loading && !entries.length && !loadFailed && (
          <p style={{ margin: 0, padding: '14px 16px', fontSize: 14, color: ui.muted }}>
            Nothing recorded yet. Changes appear here from the moment the activity log was switched on.
          </p>
        )}
        {entries.map((e) => (
          <TRow key={e.id} columns={COLS}>
            <Cell muted>{e.at ? e.at.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' } as any) : '…'}</Cell>
            <Cell>{describe(e)}</Cell>
            <Cell muted label="Who" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {who(e)}
            </Cell>
            <Cell>
              {canRestore(e) && (
                <AdminButton tone="primary" onClick={() => restore(e)} disabled={busy !== null}>
                  {busy === e.id ? 'Restoring…' : 'Restore'}
                </AdminButton>
              )}
              {!!e.restoredAt && <Pill tone="green">Restored</Pill>}
            </Cell>
          </TRow>
        ))}
      </Panel>
    </section>
  );
};

export default ActivityLog;
